"use client";

import { useState, useCallback, useEffect } from "react";
import { Modal, ModalContent } from "@/components/ui/Modal";
import { getApiUrl } from "@/lib/api";

export type PortfolioImage = { id: string; url: string };

function imageUrl(url: string): string {
  const base = getApiUrl();
  return url.startsWith("/uploads") ? `${base}/api${url}` : `${base}${url}`;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  staffName: string;
  portfolioImages: PortfolioImage[];
};

export function StaffPortfolioViewer({
  isOpen,
  onClose,
  staffName,
  portfolioImages,
}: Props) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const goPrev = useCallback(() => {
    if (portfolioImages.length === 0) return;
    setExpandedIndex((i) =>
      i === null ? null : i === 0 ? portfolioImages.length - 1 : i - 1
    );
  }, [portfolioImages.length]);

  const goNext = useCallback(() => {
    if (portfolioImages.length === 0) return;
    setExpandedIndex((i) =>
      i === null ? null : (i + 1) % portfolioImages.length
    );
  }, [portfolioImages.length]);

  useEffect(() => {
    if (!isOpen) setExpandedIndex(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || expandedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedIndex(null);
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, expandedIndex, goPrev, goNext]);

  if (!isOpen) return null;

  const isLightboxOpen = expandedIndex !== null;
  const currentImage =
    expandedIndex !== null ? portfolioImages[expandedIndex] : null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl flex flex-col">
          {/* Header */}
          <div className="shrink-0 px-5 py-4 border-b border-border/80 flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-primary"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {staffName}&apos;s portfolio
                </h3>
                <p className="text-xs text-muted-foreground">
                  {portfolioImages.length} photo{portfolioImages.length !== 1 ? "s" : ""} · Click to expand
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Gallery grid */}
          <div className="p-4 overflow-y-auto flex-1 min-h-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-3">
              {portfolioImages.map((img, index) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setExpandedIndex(index)}
                  className="group relative aspect-square rounded-xl overflow-hidden bg-muted border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-card"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={imageUrl(img.url)}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white drop-shadow-sm"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 3h6v6" />
                      <path d="M10 14 21 3" />
                      <path d="m18 13 6 6v-6" />
                      <path d="m6 6 6 6" />
                    </svg>
                    <span className="text-xs font-medium text-white drop-shadow-sm">
                      Expand
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </ModalContent>
      </Modal>

      {/* Lightbox overlay */}
      {isLightboxOpen && currentImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setExpandedIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Viewing portfolio image"
        >
          <button
            type="button"
            onClick={() => setExpandedIndex(null)}
            className="absolute top-4 right-4 z-10 rounded-full p-2 text-white/90 hover:bg-white/10 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm font-medium">
            {expandedIndex + 1} / {portfolioImages.length}
          </div>

          {/* Prev */}
          {portfolioImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-3 text-white/90 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Previous image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
          )}

          {/* Next */}
          {portfolioImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-3 text-white/90 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Next image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          )}

          {/* Image - click doesn't close so you can click the image without closing */}
          <div
            className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl(currentImage.url)}
              alt=""
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}
